import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, Plus, Settings2, Award, Loader2, Star,
  Briefcase, UserMinus, Trophy, Sparkles, Lightbulb, GraduationCap,
  Wand2, MessageSquare, Users, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = { star: Star, graduationcap: GraduationCap, lightbulb: Lightbulb, trophy: Trophy, sparkles: Sparkles };

const PerformancePage = () => {
  const { isHR } = useAuth();
  const [tab, setTab] = useState('reviews');

  // Performance state
  const [config, setConfig] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    employee_id: '', review_type: 'quarterly', overall_score: '',
    comments: '', goals_met: '', goals_next: '', period: '',
    scores: { delivery: '', quality: '', teamwork: '', initiative: '', learning: '' },
  });

  // Hiring state
  const [requisitions, setRequisitions] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [reqForm, setReqForm] = useState({
    title: '', department: '', designation: '', job_description: '',
    skills_required: '', experience_range: '', priority: 'medium',
  });
  const [exitForm, setExitForm] = useState({ employee_id: '', exit_date: '', reason: '' });

  // Publish to Careers state
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishForm, setPublishForm] = useState({
    id: '', title: '', published: false,
    location: '', employment_type: 'Full-time', seniority: '', summary: '',
  });

  // Leaderboard state
  const [lbConfig, setLbConfig] = useState(null);
  const [nominations, setNominations] = useState([]);
  const [nomOpen, setNomOpen] = useState(false);
  const [lbConfigOpen, setLbConfigOpen] = useState(false);
  const [editLbConfig, setEditLbConfig] = useState(null);
  const [nomForm, setNomForm] = useState({
    category_id: '', category_name: '', employee_id: '', employee_name: '', department: '',
    period: new Date().toISOString().slice(0, 7), reason: '',
  });

  // AI rephrase + Meetings + Succession
  const [softening, setSoftening] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ employee_id: '', title: '1:1 Meeting', scheduled_for: '', agenda: [], raw_notes: '' });
  const [meetingDetail, setMeetingDetail] = useState(null);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [structuring, setStructuring] = useState(false);
  const [succEmpId, setSuccEmpId] = useState('');
  const [succResult, setSuccResult] = useState(null);
  const [succLoading, setSuccLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [
        axios.get(`${API_URL}/api/performance/config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/performance/reviews`, { withCredentials: true }),
        axios.get(`${API_URL}/api/leaderboards/config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/leaderboards/nominations`, { withCredentials: true }),
      ];
      if (isHR) {
        calls.push(axios.get(`${API_URL}/api/employees`, { withCredentials: true }));
        calls.push(axios.get(`${API_URL}/api/hiring/requisitions`, { withCredentials: true }));
        calls.push(axios.get(`${API_URL}/api/meetings/1on1`, { withCredentials: true }));
      } else {
        calls.push(axios.get(`${API_URL}/api/meetings/1on1`, { withCredentials: true }));
      }
      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setConfig(results[0].value.data);
      if (results[1].status === 'fulfilled') setReviews(results[1].value.data);
      if (results[2].status === 'fulfilled') setLbConfig(results[2].value.data);
      if (results[3].status === 'fulfilled') setNominations(results[3].value.data);
      if (isHR) {
        if (results[4]?.status === 'fulfilled') setEmployees(results[4].value.data);
        if (results[5]?.status === 'fulfilled') setRequisitions(results[5].value.data);
        if (results[6]?.status === 'fulfilled') setMeetings(results[6].value.data);
      } else {
        if (results[4]?.status === 'fulfilled') setMeetings(results[4].value.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isHR]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- Reviews ----
  const handleSubmitReview = async () => {
    if (!form.employee_id || form.overall_score === '') { toast.error('Select employee and score'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/performance/reviews`, {
        employee_id: form.employee_id, review_type: form.review_type,
        overall_score: parseFloat(form.overall_score), comments: form.comments,
        scores: form.scores, period: form.period || undefined,
        goals_met: form.goals_met ? form.goals_met.split('\n').filter(Boolean) : [],
        goals_next: form.goals_next ? form.goals_next.split('\n').filter(Boolean) : [],
      }, { withCredentials: true });
      toast.success('Review submitted');
      setReviewOpen(false);
      setForm({ employee_id: '', review_type: 'quarterly', overall_score: '', comments: '', goals_met: '', goals_next: '', period: '', scores: { delivery: '', quality: '', teamwork: '', initiative: '', learning: '' } });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/performance/config`, editConfig, { withCredentials: true });
      toast.success('Config saved');
      setConfigOpen(false);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const getRatingBadge = (rating) => {
    const level = config?.rating_levels?.find(l => l.key === rating);
    if (!level) return <Badge variant="outline">{rating}</Badge>;
    return <Badge style={{ backgroundColor: level.color + '20', color: level.color, border: `1px solid ${level.color}40` }}>{level.label}</Badge>;
  };

  // ---- Hiring ----
  const handleCreateReq = async () => {
    if (!reqForm.title || !reqForm.designation) { toast.error('Title & designation required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/hiring/requisitions`, {
        ...reqForm,
        skills_required: reqForm.skills_required ? reqForm.skills_required.split(',').map(s => s.trim()).filter(Boolean) : [],
      }, { withCredentials: true });
      toast.success('Requisition created');
      setReqOpen(false);
      setReqForm({ title: '', department: '', designation: '', job_description: '', skills_required: '', experience_range: '', priority: 'medium' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleProcessExit = async () => {
    if (!exitForm.employee_id || !exitForm.exit_date) { toast.error('Employee and exit date required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/employees/${exitForm.employee_id}/exit`, {
        exit_date: exitForm.exit_date, reason: exitForm.reason,
      }, { withCredentials: true });
      toast.success(res.data?.auto_requisition ? 'Exit processed — auto-requisition created' : 'Exit processed');
      setExitOpen(false);
      setExitForm({ employee_id: '', exit_date: '', reason: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleUpdateReqStatus = async (reqId, status) => {
    try {
      await axios.put(`${API_URL}/api/hiring/requisitions/${reqId}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status}`);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const openPublishDialog = (req) => {
    setPublishForm({
      id: req.id,
      title: req.title || '',
      published: !!req.published,
      location: req.location || '',
      employment_type: req.employment_type || 'Full-time',
      seniority: req.seniority || '',
      summary: req.summary || '',
    });
    setPublishOpen(true);
  };

  const handleSavePublish = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/hiring/requisitions/${publishForm.id}`, {
        published: publishForm.published,
        location: publishForm.location,
        employment_type: publishForm.employment_type,
        seniority: publishForm.seniority,
        summary: publishForm.summary,
      }, { withCredentials: true });
      toast.success(publishForm.published ? 'Published to wisewit.ai/careers' : 'Unpublished from wisewit.ai/careers');
      setPublishOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSubmitting(false); }
  };

  // ---- Leaderboards ----
  const handleNominate = async () => {
    if (!nomForm.category_id || !nomForm.employee_id) { toast.error('Select category & employee'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leaderboards/nominations`, nomForm, { withCredentials: true });
      toast.success('Nomination recorded');
      setNomOpen(false);
      setNomForm({ category_id: '', category_name: '', employee_id: '', employee_name: '', department: '', period: new Date().toISOString().slice(0, 7), reason: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleSaveLbConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/leaderboards/config`, editLbConfig, { withCredentials: true });
      toast.success('Categories updated');
      setLbConfigOpen(false);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  // ---- AI Rephrase ----
  const handleSoften = async () => {
    if (!form.comments?.trim()) { toast.error('Write something first'); return; }
    setSoftening(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/rephrase-feedback`, { text: form.comments, tone: 'constructive' }, { withCredentials: true });
      setForm({ ...form, comments: res.data.rewritten });
      toast.success('Feedback softened with AI');
    } catch (err) { toast.error(err.response?.data?.detail || 'AI rephrase failed'); }
    finally { setSoftening(false); }
  };

  // ---- Meetings ----
  const handleCreateMeeting = async () => {
    if (!meetingForm.employee_id || !meetingForm.scheduled_for) { toast.error('Employee and date required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/meetings/1on1`, meetingForm, { withCredentials: true });
      toast.success('Meeting scheduled');
      setMeetingOpen(false);
      setMeetingDetail(res.data);
      setMeetingForm({ employee_id: '', title: '1:1 Meeting', scheduled_for: '', agenda: [], raw_notes: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleSuggestAgenda = async (empId) => {
    if (!empId) { toast.error('Select an employee first'); return; }
    setAgendaLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/meeting-agenda/${empId}`, {}, { withCredentials: true });
      setMeetingForm(prev => ({ ...prev, agenda: res.data.agenda || [] }));
      toast.success('Agenda generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setAgendaLoading(false); }
  };

  const handleStructureNotes = async (meeting) => {
    if (!meeting.raw_notes?.trim()) { toast.error('No raw notes to structure'); return; }
    setStructuring(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/structure-notes`, { raw_notes: meeting.raw_notes }, { withCredentials: true });
      const updated = await axios.put(`${API_URL}/api/meetings/1on1/${meeting.id}`, {
        structured_notes: res.data,
        action_items: res.data.action_items || [],
        status: 'completed',
      }, { withCredentials: true });
      toast.success('Notes structured by AI');
      setMeetingDetail(updated.data);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setStructuring(false); }
  };

  const handleUpdateMeetingNotes = async (raw) => {
    if (!meetingDetail) return;
    try {
      const res = await axios.put(`${API_URL}/api/meetings/1on1/${meetingDetail.id}`, { raw_notes: raw }, { withCredentials: true });
      setMeetingDetail(res.data);
    } catch (err) { /* silent */ }
  };

  // ---- Succession ----
  const handleRunSuccession = async () => {
    if (!succEmpId) { toast.error('Select an employee'); return; }
    setSuccLoading(true);
    setSuccResult(null);
    try {
      const res = await axios.get(`${API_URL}/api/ai/succession/${succEmpId}`, { withCredentials: true });
      setSuccResult(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSuccLoading(false); }
  };

  // Group nominations by category for leaderboard display
  const nominationsByCategory = (lbConfig?.categories || []).map(cat => ({
    ...cat,
    nominations: nominations.filter(n => n.category_id === cat.id),
  }));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="performance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><TrendingUp className="w-7 h-7 text-emerald-500" /> Performance</h1>
          <p className="text-muted-foreground text-sm">Reviews, Hiring Requisitions & Recognition Leaderboards</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
          {isHR && <TabsTrigger value="hiring" data-testid="tab-hiring">Hiring</TabsTrigger>}
          <TabsTrigger value="meetings" data-testid="tab-meetings">1:1 Meetings</TabsTrigger>
          {isHR && <TabsTrigger value="succession" data-testid="tab-succession">Succession</TabsTrigger>}
          <TabsTrigger value="leaderboards" data-testid="tab-leaderboards">Leaderboards</TabsTrigger>
        </TabsList>

        {/* ============ REVIEWS TAB ============ */}
        <TabsContent value="reviews" className="mt-4 space-y-6">
          <div className="flex items-center justify-end gap-2">
            {isHR && <Button className="gap-2" onClick={() => setReviewOpen(true)} data-testid="new-review-btn"><Plus className="w-4 h-4" /> New Review</Button>}
            {isHR && <Button variant="outline" className="gap-2" onClick={() => { setEditConfig(config ? JSON.parse(JSON.stringify(config)) : {}); setConfigOpen(true); }} data-testid="config-btn"><Settings2 className="w-4 h-4" /> Configure</Button>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {config?.rating_levels?.map(lvl => (
              <Card key={lvl.key} className="text-center" style={{ borderColor: lvl.color + '40' }} data-testid={`rating-card-${lvl.key}`}>
                <CardContent className="p-4">
                  <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center mb-2" style={{ backgroundColor: lvl.color + '20' }}>
                    <Star className="w-5 h-5" style={{ color: lvl.color }} />
                  </div>
                  <p className="font-bold" style={{ color: lvl.color }}>{lvl.label}</p>
                  <p className="text-xs text-muted-foreground">{lvl.min_score}–{lvl.max_score} score</p>
                  <p className="text-sm font-semibold mt-1">{lvl.hike_percentage}% hike</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">{isHR ? 'All Reviews' : 'My Reviews'}</CardTitle><CardDescription>{reviews.length} review(s)</CardDescription></CardHeader>
            <CardContent>
              {reviews.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr>{isHR && <th>Employee</th>}<th>Period</th><th>Type</th><th>Score</th><th>Rating</th><th>Hike %</th><th>Reviewer</th><th>Date</th></tr></thead>
                    <tbody>
                      {reviews.map(r => {
                        const lvl = config?.rating_levels?.find(l => l.key === r.rating);
                        return (
                          <tr key={r.id} data-testid={`review-row-${r.id}`}>
                            {isHR && <td className="font-medium text-sm">{r.employee_name}</td>}
                            <td className="font-mono text-sm">{r.period}</td>
                            <td><Badge variant="outline" className="text-xs">{r.review_type}</Badge></td>
                            <td>
                              <div className="flex items-center gap-2">
                                <span className="font-bold">{r.overall_score}</span>
                                <Progress value={r.overall_score} className="h-1.5 w-16" />
                              </div>
                            </td>
                            <td>{getRatingBadge(r.rating)}</td>
                            <td className="font-mono text-sm font-semibold text-emerald-600">{lvl?.hike_percentage || 0}%</td>
                            <td className="text-sm text-muted-foreground">{r.reviewer_name}</td>
                            <td className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('en-IN')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-center text-muted-foreground py-8">{isHR ? 'No reviews yet. Start the first review cycle!' : 'No performance reviews yet.'}</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ HIRING TAB ============ */}
        {isHR && (
          <TabsContent value="hiring" className="mt-4 space-y-6">
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" className="gap-2" onClick={() => { setExitForm({ employee_id: '', exit_date: new Date().toISOString().slice(0, 10), reason: '' }); setExitOpen(true); }} data-testid="process-exit-btn">
                <UserMinus className="w-4 h-4" /> Process Exit
              </Button>
              <Button className="gap-2" onClick={() => setReqOpen(true)} data-testid="new-req-btn">
                <Plus className="w-4 h-4" /> New Requisition
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-4 h-4" /> Open Requisitions</CardTitle>
                <CardDescription>{requisitions.length} total — auto-triggered upon employee exits</CardDescription>
              </CardHeader>
              <CardContent>
                {requisitions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {requisitions.map(r => (
                      <Card key={r.id} className="border-l-4" style={{ borderLeftColor: r.priority === 'high' ? '#ef4444' : r.priority === 'medium' ? '#f59e0b' : '#10b981' }} data-testid={`req-card-${r.id}`}>
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm">{r.title}</p>
                              <p className="text-xs text-muted-foreground">{r.designation} · {r.department}</p>
                            </div>
                            <Badge variant={r.status === 'open' ? 'default' : 'outline'} className="text-xs">{r.status}</Badge>
                          </div>
                          {r.triggered_by === 'auto_exit' && (
                            <Badge variant="outline" className="text-xs gap-1 bg-amber-50 dark:bg-amber-900/20 border-amber-300 text-amber-700 dark:text-amber-400">
                              <Sparkles className="w-3 h-3" /> Auto-triggered: Replacement for {r.exit_employee_name}
                            </Badge>
                          )}
                          {r.skills_required?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {r.skills_required.slice(0, 5).map((s, i) => <Badge key={i} variant="secondary" className="text-[10px]">{s}</Badge>)}
                            </div>
                          )}
                          {r.experience_range && <p className="text-xs text-muted-foreground">Experience: {r.experience_range}</p>}
                          {r.job_description && <p className="text-xs text-muted-foreground line-clamp-2">{r.job_description}</p>}
                          {r.published && (
                            <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 text-emerald-700 dark:text-emerald-400" data-testid={`req-published-${r.id}`}>
                              <Globe className="w-3 h-3" /> Live on wisewit.ai/careers
                            </Badge>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {r.status === 'open' && (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUpdateReqStatus(r.id, 'in_progress')} data-testid={`req-progress-${r.id}`}>Mark In-Progress</Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUpdateReqStatus(r.id, 'closed')} data-testid={`req-close-${r.id}`}>Close</Button>
                              </>
                            )}
                            {r.status === 'in_progress' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUpdateReqStatus(r.id, 'closed')} data-testid={`req-close-${r.id}`}>Close</Button>
                            )}
                            {r.status !== 'closed' && (
                              <Button size="sm" variant={r.published ? 'default' : 'outline'} className="h-7 text-xs gap-1" onClick={() => openPublishDialog(r)} data-testid={`req-publish-${r.id}`}>
                                <Globe className="w-3 h-3" /> {r.published ? 'Edit public listing' : 'Publish to careers'}
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : <p className="text-center text-muted-foreground py-8">No requisitions yet</p>}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ============ MEETINGS TAB ============ */}
        <TabsContent value="meetings" className="mt-4 space-y-4">
          {isHR && (
            <div className="flex items-center justify-end">
              <Button className="gap-2" onClick={() => { setMeetingForm({ employee_id: '', title: '1:1 Meeting', scheduled_for: new Date().toISOString().slice(0, 10), agenda: [], raw_notes: '' }); setMeetingOpen(true); }} data-testid="new-meeting-btn">
                <Plus className="w-4 h-4" /> Schedule 1:1
              </Button>
            </div>
          )}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> 1:1 Meetings</CardTitle><CardDescription>{meetings.length} meeting(s) — AI helps draft agendas & structure notes</CardDescription></CardHeader>
            <CardContent>
              {meetings.length > 0 ? (
                <div className="space-y-2">
                  {meetings.map(m => (
                    <div key={m.id} className="flex items-center justify-between p-3 rounded border hover:bg-muted/40 cursor-pointer" onClick={() => setMeetingDetail(m)} data-testid={`meeting-row-${m.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{m.title} — <span className="text-muted-foreground font-normal">{m.employee_name || 'Self'}</span></p>
                        <p className="text-xs text-muted-foreground">Scheduled: {m.scheduled_for} · Status: {m.status}</p>
                      </div>
                      <Badge variant={m.status === 'completed' ? 'default' : 'outline'} className="text-xs">{m.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : <p className="text-center text-muted-foreground py-8 text-sm">No meetings yet</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ SUCCESSION TAB ============ */}
        {isHR && (
          <TabsContent value="succession" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Skill-Gap & Succession Planning</CardTitle>
                <CardDescription>AI analyzes performance + tech stack to recommend internal successors</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Select value={succEmpId} onValueChange={setSuccEmpId}>
                    <SelectTrigger className="flex-1" data-testid="succession-employee"><SelectValue placeholder="Select employee whose successor to plan" /></SelectTrigger>
                    <SelectContent>{employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={handleRunSuccession} disabled={!succEmpId || succLoading} className="gap-2" data-testid="run-succession-btn">
                    {succLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Analyze
                  </Button>
                </div>

                {succResult && (
                  <div className="space-y-3 pt-2">
                    <div className="p-3 rounded border bg-purple-50 dark:bg-purple-900/10 text-sm" data-testid="succession-narrative">
                      <p className="text-xs uppercase tracking-wider font-semibold text-purple-700 dark:text-purple-400 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Recommendation</p>
                      <p className="whitespace-pre-wrap">{succResult.narrative}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {(succResult.top_successors || []).map((s, i) => (
                        <Card key={s.id} className={i === 0 ? 'border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10 dark:border-emerald-900/40' : ''}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <Badge className={i === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-muted'}>#{i + 1}</Badge>
                              <span className="text-xs font-mono text-muted-foreground">Score: {s.score}</span>
                            </div>
                            <p className="font-semibold text-sm">{s.name}</p>
                            <p className="text-xs text-muted-foreground">{s.designation}</p>
                            <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
                              <div>Rating: <span className="font-semibold capitalize">{s.rating}</span></div>
                              <div>Skills: <span className="font-semibold">{s.skill_overlap}</span></div>
                              <div>Tenure: <span className="font-semibold">{s.tenure_years}y</span></div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    {succResult.skill_gaps?.length > 0 && (
                      <div className="p-3 rounded border bg-amber-50 dark:bg-amber-900/10 text-sm">
                        <p className="text-xs uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-400 mb-1">Skill Gaps to Address</p>
                        <div className="flex flex-wrap gap-1">
                          {succResult.skill_gaps.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s}</Badge>)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ============ LEADERBOARDS TAB ============ */}
        <TabsContent value="leaderboards" className="mt-4 space-y-6">
          {isHR && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" className="gap-2" onClick={() => { setEditLbConfig(lbConfig ? JSON.parse(JSON.stringify(lbConfig)) : { categories: [] }); setLbConfigOpen(true); }} data-testid="lb-config-btn">
                <Settings2 className="w-4 h-4" /> Manage Categories
              </Button>
              <Button className="gap-2" onClick={() => setNomOpen(true)} data-testid="nominate-btn">
                <Trophy className="w-4 h-4" /> Nominate
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {nominationsByCategory.filter(c => c.active !== false).map(cat => {
              const Icon = ICONS[cat.icon?.toLowerCase()] || Trophy;
              // Tally by employee for this period
              const tally = {};
              cat.nominations.forEach(n => {
                tally[n.employee_id] = tally[n.employee_id] || { name: n.employee_name, dept: n.department, count: 0 };
                tally[n.employee_id].count++;
              });
              const ranked = Object.entries(tally).sort((a, b) => b[1].count - a[1].count).slice(0, 5);
              return (
                <Card key={cat.id} data-testid={`lb-cat-${cat.id}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2"><Icon className="w-5 h-5 text-amber-500" /> {cat.name}</CardTitle>
                    <CardDescription className="text-xs">{cat.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {ranked.length > 0 ? (
                      <div className="space-y-2">
                        {ranked.map(([empId, data], i) => (
                          <div key={empId} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : i === 1 ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300' : i === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-muted text-muted-foreground'}`}>
                              {i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{data.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{data.dept}</p>
                            </div>
                            <Badge variant="outline" className="text-xs">{data.count} pts</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-center text-muted-foreground py-4">No nominations yet</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {(!lbConfig?.categories || lbConfig.categories.length === 0) && (
              <Card className="md:col-span-2 lg:col-span-3"><CardContent className="py-10 text-center text-muted-foreground text-sm">No leaderboard categories configured.{isHR && ' Click "Manage Categories" to add some.'}</CardContent></Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* === Review Dialog === */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Performance Review</DialogTitle><DialogDescription>Capture an employee's quarterly/annual performance with dimension scores. Rating tier auto-determined from overall score.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="review-employee"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Type</Label>
                <Select value={form.review_type} onValueChange={v => setForm({ ...form, review_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="mid_year">Mid-Year</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="continuous">Continuous</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Period</Label><Input value={form.period} onChange={e => setForm({ ...form, period: e.target.value })} placeholder="e.g., 2026-Q1" /></div>
            </div>
            <div className="space-y-2"><Label>Overall Score (0-100)</Label><Input type="number" min="0" max="100" value={form.overall_score} onChange={e => setForm({ ...form, overall_score: e.target.value })} data-testid="review-score" /></div>
            {form.overall_score !== '' && config?.rating_levels && (
              <div className="p-2 rounded bg-muted/50 text-sm text-center">
                Rating: {getRatingBadge(config.rating_levels.find(l => parseFloat(form.overall_score) >= l.min_score && parseFloat(form.overall_score) <= l.max_score)?.key || 'cadet')}
                {' → '}
                <span className="font-semibold text-emerald-600">{config.rating_levels.find(l => parseFloat(form.overall_score) >= l.min_score && parseFloat(form.overall_score) <= l.max_score)?.hike_percentage || 0}% hike</span>
              </div>
            )}
            <div className="space-y-2"><Label>Dimension Scores</Label>
              <div className="grid grid-cols-2 gap-2">
                {['delivery', 'quality', 'teamwork', 'initiative', 'learning'].map(dim => (
                  <div key={dim} className="flex items-center gap-2">
                    <span className="text-xs w-20 capitalize">{dim}</span>
                    <Input type="number" min="0" max="100" className="h-8" value={form.scores[dim]} onChange={e => setForm({ ...form, scores: { ...form.scores, [dim]: e.target.value } })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Comments</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 text-xs text-purple-600 dark:text-purple-400" onClick={handleSoften} disabled={softening || !form.comments?.trim()} data-testid="soften-feedback-btn">
                  {softening ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Soften with AI
                </Button>
              </div>
              <Textarea value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} rows={3} placeholder="Specific feedback — AI can soften the tone before saving" />
            </div>
            <div className="space-y-2"><Label>Goals Met (one per line)</Label><Textarea value={form.goals_met} onChange={e => setForm({ ...form, goals_met: e.target.value })} rows={2} placeholder="Delivered feature X on time" /></div>
            <div className="space-y-2"><Label>Goals for Next Period (one per line)</Label><Textarea value={form.goals_next} onChange={e => setForm({ ...form, goals_next: e.target.value })} rows={2} /></div>
            <Button className="w-full gap-2" onClick={handleSubmitReview} disabled={submitting} data-testid="submit-review-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />} Submit Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Performance Config Dialog === */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Performance Configuration</DialogTitle><DialogDescription>Define rating levels and salary hike percentages</DialogDescription></DialogHeader>
          {editConfig && (
            <div className="space-y-4">
              <Label className="font-semibold">Rating Levels</Label>
              {editConfig.rating_levels?.map((lvl, i) => (
                <div key={i} className="p-3 rounded-lg border space-y-2" style={{ borderColor: lvl.color + '40' }}>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={lvl.label} onChange={e => { const ls = [...editConfig.rating_levels]; ls[i] = { ...ls[i], label: e.target.value }; setEditConfig({ ...editConfig, rating_levels: ls }); }} placeholder="Label" />
                    <Input value={lvl.color} onChange={e => { const ls = [...editConfig.rating_levels]; ls[i] = { ...ls[i], color: e.target.value }; setEditConfig({ ...editConfig, rating_levels: ls }); }} placeholder="#hex color" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Min Score</Label><Input type="number" value={lvl.min_score} onChange={e => { const ls = [...editConfig.rating_levels]; ls[i] = { ...ls[i], min_score: parseInt(e.target.value) }; setEditConfig({ ...editConfig, rating_levels: ls }); }} /></div>
                    <div><Label className="text-xs">Max Score</Label><Input type="number" value={lvl.max_score} onChange={e => { const ls = [...editConfig.rating_levels]; ls[i] = { ...ls[i], max_score: parseInt(e.target.value) }; setEditConfig({ ...editConfig, rating_levels: ls }); }} /></div>
                    <div><Label className="text-xs">Hike %</Label><Input type="number" value={lvl.hike_percentage} onChange={e => { const ls = [...editConfig.rating_levels]; ls[i] = { ...ls[i], hike_percentage: parseInt(e.target.value) }; setEditConfig({ ...editConfig, rating_levels: ls }); }} /></div>
                  </div>
                </div>
              ))}
              <div className="space-y-2"><Label>Badge Weight in Score (%)</Label><Input type="number" value={editConfig.badge_weight || 10} onChange={e => setEditConfig({ ...editConfig, badge_weight: parseInt(e.target.value) })} /></div>
              <Button className="w-full" onClick={handleSaveConfig} data-testid="save-config-btn">Save Configuration</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === New Requisition Dialog === */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Hiring Requisition</DialogTitle><DialogDescription>Open a role for hiring. Used by HR/Talent to track open positions.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Title</Label><Input value={reqForm.title} onChange={e => setReqForm({ ...reqForm, title: e.target.value })} placeholder="Senior Backend Engineer" data-testid="req-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Department</Label><Input value={reqForm.department} onChange={e => setReqForm({ ...reqForm, department: e.target.value })} /></div>
              <div className="space-y-2"><Label>Designation</Label><Input value={reqForm.designation} onChange={e => setReqForm({ ...reqForm, designation: e.target.value })} data-testid="req-designation" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Experience Range</Label><Input value={reqForm.experience_range} onChange={e => setReqForm({ ...reqForm, experience_range: e.target.value })} placeholder="3-5 years" /></div>
              <div className="space-y-2"><Label>Priority</Label>
                <Select value={reqForm.priority} onValueChange={v => setReqForm({ ...reqForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Skills Required (comma-separated)</Label><Input value={reqForm.skills_required} onChange={e => setReqForm({ ...reqForm, skills_required: e.target.value })} placeholder="Python, FastAPI, MongoDB" /></div>
            <div className="space-y-2"><Label>Job Description</Label><Textarea value={reqForm.job_description} onChange={e => setReqForm({ ...reqForm, job_description: e.target.value })} rows={3} /></div>
            <Button className="w-full" onClick={handleCreateReq} disabled={submitting} data-testid="submit-req-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Requisition'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Publish to Careers Dialog === */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe className="w-4 h-4 text-emerald-600" /> Publish to wisewit.ai/careers</DialogTitle>
            <DialogDescription>Show <span className="font-medium">{publishForm.title}</span> on the public careers page. Candidates who apply land directly in your ATS.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md border p-3 bg-muted/30">
              <div>
                <p className="text-sm font-medium">Publish this role</p>
                <p className="text-xs text-muted-foreground">Toggle off any time to remove from the public site.</p>
              </div>
              <Switch checked={publishForm.published} onCheckedChange={v => setPublishForm({ ...publishForm, published: v })} data-testid="publish-toggle" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={publishForm.location} onChange={e => setPublishForm({ ...publishForm, location: e.target.value })} placeholder="Hyderabad / Remote" data-testid="publish-location" />
              </div>
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select value={publishForm.employment_type} onValueChange={v => setPublishForm({ ...publishForm, employment_type: v })}>
                  <SelectTrigger data-testid="publish-employment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Seniority</Label>
              <Select value={publishForm.seniority} onValueChange={v => setPublishForm({ ...publishForm, seniority: v })}>
                <SelectTrigger data-testid="publish-seniority"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Junior">Junior (0–2 yrs)</SelectItem>
                  <SelectItem value="Mid">Mid (2–5 yrs)</SelectItem>
                  <SelectItem value="Senior">Senior (5–8 yrs)</SelectItem>
                  <SelectItem value="Staff">Staff / Lead (8+ yrs)</SelectItem>
                  <SelectItem value="Principal">Principal (10+ yrs)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Public Summary</Label>
              <Textarea value={publishForm.summary} onChange={e => setPublishForm({ ...publishForm, summary: e.target.value })} rows={4} placeholder="One-paragraph pitch that shows on the careers page card. If left empty, the first 200 chars of the job description are used." data-testid="publish-summary" />
              <p className="text-[11px] text-muted-foreground">The full Job Description is shown when a candidate clicks through.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setPublishOpen(false)}>Cancel</Button>
              <Button className="flex-1" onClick={handleSavePublish} disabled={submitting} data-testid="publish-save-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Process Exit Dialog === */}
      <Dialog open={exitOpen} onOpenChange={setExitOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Process Employee Exit</DialogTitle><DialogDescription>An auto-requisition will be created if a matching profile is configured</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Employee</Label>
              <Select value={exitForm.employee_id} onValueChange={v => setExitForm({ ...exitForm, employee_id: v })}>
                <SelectTrigger data-testid="exit-employee"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Exit Date</Label><Input type="date" value={exitForm.exit_date} onChange={e => setExitForm({ ...exitForm, exit_date: e.target.value })} data-testid="exit-date" /></div>
            <div className="space-y-2"><Label>Reason</Label><Textarea value={exitForm.reason} onChange={e => setExitForm({ ...exitForm, reason: e.target.value })} rows={2} /></div>
            <Button className="w-full" onClick={handleProcessExit} disabled={submitting} data-testid="submit-exit-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Process Exit'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Nominate Dialog === */}
      <Dialog open={nomOpen} onOpenChange={setNomOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nominate for Leaderboard</DialogTitle><DialogDescription>Recognize an employee for a non-monetary leaderboard category.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Category</Label>
              <Select value={nomForm.category_id} onValueChange={v => {
                const cat = lbConfig?.categories?.find(c => c.id === v);
                setNomForm({ ...nomForm, category_id: v, category_name: cat?.name || '' });
              }}>
                <SelectTrigger data-testid="nom-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{(lbConfig?.categories || []).filter(c => c.active !== false).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Employee</Label>
              <Select value={nomForm.employee_id} onValueChange={v => {
                const emp = employees.find(e => e.id === v);
                setNomForm({ ...nomForm, employee_id: v, employee_name: emp?.name || '', department: emp?.department || '' });
              }}>
                <SelectTrigger data-testid="nom-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Period</Label><Input value={nomForm.period} onChange={e => setNomForm({ ...nomForm, period: e.target.value })} placeholder="2026-02" /></div>
            <div className="space-y-2"><Label>Reason</Label><Textarea value={nomForm.reason} onChange={e => setNomForm({ ...nomForm, reason: e.target.value })} rows={3} placeholder="Why are you nominating this person?" /></div>
            <Button className="w-full" onClick={handleNominate} disabled={submitting} data-testid="submit-nom-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Nomination'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Leaderboard Config Dialog === */}
      <Dialog open={lbConfigOpen} onOpenChange={setLbConfigOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage Leaderboard Categories</DialogTitle><DialogDescription>Add, edit, or remove recognition categories shown on the leaderboards tab.</DialogDescription></DialogHeader>
          {editLbConfig && (
            <div className="space-y-3">
              {(editLbConfig.categories || []).map((cat, i) => (
                <div key={cat.id || i} className="p-3 rounded border space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={cat.name || ''} onChange={e => { const cs = [...editLbConfig.categories]; cs[i] = { ...cs[i], name: e.target.value }; setEditLbConfig({ ...editLbConfig, categories: cs }); }} placeholder="Category name" />
                    <Select value={cat.icon || 'trophy'} onValueChange={v => { const cs = [...editLbConfig.categories]; cs[i] = { ...cs[i], icon: v }; setEditLbConfig({ ...editLbConfig, categories: cs }); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trophy">Trophy</SelectItem>
                        <SelectItem value="star">Star</SelectItem>
                        <SelectItem value="graduationcap">Learning</SelectItem>
                        <SelectItem value="lightbulb">Innovation</SelectItem>
                        <SelectItem value="sparkles">Sparkles</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea value={cat.description || ''} onChange={e => { const cs = [...editLbConfig.categories]; cs[i] = { ...cs[i], description: e.target.value }; setEditLbConfig({ ...editLbConfig, categories: cs }); }} placeholder="Description" rows={2} />
                  <div className="flex justify-between items-center">
                    <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={cat.active !== false} onChange={e => { const cs = [...editLbConfig.categories]; cs[i] = { ...cs[i], active: e.target.checked }; setEditLbConfig({ ...editLbConfig, categories: cs }); }} /> Active</label>
                    <Button size="sm" variant="ghost" className="text-destructive h-7 text-xs" onClick={() => { const cs = editLbConfig.categories.filter((_, idx) => idx !== i); setEditLbConfig({ ...editLbConfig, categories: cs }); }}>Remove</Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" className="w-full gap-2" onClick={() => {
                const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `cat-${Date.now()}`;
                setEditLbConfig({ ...editLbConfig, categories: [...(editLbConfig.categories || []), { id, name: '', description: '', icon: 'trophy', active: true }] });
              }} data-testid="add-cat-btn"><Plus className="w-4 h-4" /> Add Category</Button>
              <Button className="w-full" onClick={handleSaveLbConfig} data-testid="save-lb-config-btn">Save Categories</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* === Schedule Meeting Dialog === */}
      <Dialog open={meetingOpen} onOpenChange={setMeetingOpen}>
        <DialogContent className="sm:max-w-md" data-testid="meeting-dialog">
          <DialogHeader><DialogTitle>Schedule 1:1 Meeting</DialogTitle><DialogDescription>AI can suggest an agenda based on the employee's recent performance & OKRs.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Employee</Label>
              <Select value={meetingForm.employee_id} onValueChange={v => setMeetingForm({ ...meetingForm, employee_id: v })}>
                <SelectTrigger data-testid="meeting-employee"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2"><Label>Title</Label><Input value={meetingForm.title} onChange={e => setMeetingForm({ ...meetingForm, title: e.target.value })} /></div>
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={meetingForm.scheduled_for} onChange={e => setMeetingForm({ ...meetingForm, scheduled_for: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Agenda</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 text-xs text-purple-600" onClick={() => handleSuggestAgenda(meetingForm.employee_id)} disabled={!meetingForm.employee_id || agendaLoading} data-testid="suggest-agenda-btn">
                  {agendaLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Suggest with AI
                </Button>
              </div>
              <Textarea value={(meetingForm.agenda || []).join('\n')} onChange={e => setMeetingForm({ ...meetingForm, agenda: e.target.value.split('\n').filter(Boolean) })} rows={5} placeholder="One agenda item per line" />
            </div>
            <Button className="w-full" onClick={handleCreateMeeting} disabled={submitting} data-testid="create-meeting-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Schedule'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Meeting Detail Dialog === */}
      <Dialog open={!!meetingDetail} onOpenChange={(o) => !o && setMeetingDetail(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="meeting-detail-dialog">
          <DialogHeader>
            <DialogTitle>{meetingDetail?.title}</DialogTitle>
            <DialogDescription>{meetingDetail?.employee_name} · {meetingDetail?.scheduled_for} · {meetingDetail?.status}</DialogDescription>
          </DialogHeader>
          {meetingDetail && (
            <div className="space-y-4">
              {meetingDetail.agenda?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Agenda</p>
                  <ul className="text-sm space-y-1 list-disc list-inside">{meetingDetail.agenda.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Raw Notes</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 text-xs text-purple-600" onClick={() => handleStructureNotes(meetingDetail)} disabled={structuring || !meetingDetail.raw_notes?.trim()} data-testid="structure-notes-btn">
                    {structuring ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Structure with AI
                  </Button>
                </div>
                <Textarea value={meetingDetail.raw_notes || ''} onChange={e => { const v = e.target.value; setMeetingDetail({ ...meetingDetail, raw_notes: v }); }} onBlur={(e) => handleUpdateMeetingNotes(e.target.value)} rows={6} placeholder="Paste raw notes here, then click 'Structure with AI'" />
              </div>
              {meetingDetail.structured_notes && (meetingDetail.structured_notes.summary || meetingDetail.structured_notes.action_items?.length > 0) && (
                <div className="space-y-2 p-3 rounded border bg-purple-50 dark:bg-purple-900/10">
                  <p className="text-xs font-semibold uppercase text-purple-700 dark:text-purple-400 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI-Structured</p>
                  {meetingDetail.structured_notes.summary && <p className="text-sm"><span className="font-semibold">Summary:</span> {meetingDetail.structured_notes.summary}</p>}
                  {meetingDetail.structured_notes.action_items?.length > 0 && (
                    <div><p className="text-xs font-semibold mt-1">Action Items:</p><ul className="text-sm list-disc list-inside">{meetingDetail.structured_notes.action_items.map((a, i) => <li key={i}>{a}</li>)}</ul></div>
                  )}
                  {meetingDetail.structured_notes.decisions?.length > 0 && (
                    <div><p className="text-xs font-semibold mt-1">Decisions:</p><ul className="text-sm list-disc list-inside">{meetingDetail.structured_notes.decisions.map((a, i) => <li key={i}>{a}</li>)}</ul></div>
                  )}
                  {meetingDetail.structured_notes.follow_ups?.length > 0 && (
                    <div><p className="text-xs font-semibold mt-1">Follow-ups:</p><ul className="text-sm list-disc list-inside">{meetingDetail.structured_notes.follow_ups.map((a, i) => <li key={i}>{a}</li>)}</ul></div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformancePage;
